<script setup lang="ts">
import { computed } from 'vue';
import { ChevronDownIcon } from '@heroicons/vue/24/solid';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import LzpLogFilterRules from '@/components/DataTable/LzpLogFilterRules.vue';
import TrackCard from '@/components/TrackCard.vue';
import LzpLogMessage from '@/components/DataTable/LzpLogMessage.vue';
import { useInspectorSections } from '@/composables/useInspectorSections';
import { formatUsMmDdYyTime12h } from '@/utils/date';
import { playlistFileNameInLogMessage } from '@/utils/playerLogPlaylist';
import { useOpenCurrentPlaylist } from '@/composables/useOpenCurrentPlaylist';
import type {
    PlayerLogEntry,
    PlayerLogRange,
    PlayerLogsMeta,
    PlayerLogTrack,
} from '@/composables/api/useLocationZonePlayerInspectorApi';
import type { PlaylistTrackRow } from '@/composables/api/usePlaylistSwitchingApi';

/**
 * Collapsible "Logs" card for the player inspector — every entry the player
 * reports (plays, syncs, command output, …) from LzpLogs2, most recent first.
 * Filters (time range, log type, message substring, track) are applied
 * server-side by
 * the parent's query. Open state remembered per entity.
 */
const props = defineProps<{
    logs: PlayerLogEntry[];
    meta: PlayerLogsMeta | null;
    isLoading: boolean;
    isError: boolean;
    /** Scopes a PLAYLIST line's filename to this player's zone when resolving it. */
    locationZonePlayerId?: number | null;
}>();

const { openPlaylist } = useOpenCurrentPlaylist(computed(() => props.locationZonePlayerId ?? null));

const range = defineModel<PlayerLogRange>('range', { required: true });
const messageFilter = defineModel<string>('messageFilter', { required: true });
const messageMode = defineModel<'contains' | 'not_contains'>('messageMode', { required: true });
const trackFilter = defineModel<string>('trackFilter', { required: true });
const types = defineModel<string[]>('types', { required: true });
const typesMode = defineModel<'in' | 'not_in'>('typesMode', { required: true });

const filtersActive = computed(
    () =>
        range.value !== 'all'
        || types.value.length > 0
        || messageFilter.value.trim() !== ''
        || trackFilter.value.trim() !== ''
);



/**
 * A log entry's track in the shape the shared track card takes. The log payload
 * carries only what the card shows at compact density, so the rest is null —
 * the card renders those densities' extras (rating, tags, code) only when asked
 * for them, which the logs never are.
 */
function logTrackRow(track: PlayerLogTrack): PlaylistTrackRow {
    return {
        id: track.id,
        code: track.code,
        name: track.name,
        remix_name: track.remix_name,
        artist_id: track.artist_id,
        artist_name: track.artist_name,
        album_id: track.album_id,
        album_name: track.album_name,
        genre_name: null,
        rating: null,
        playlist_count: null,
        isrc: null,
        bitrate: null,
        duration: track.duration,
        copyright: null,
        cover_art_signed_url: track.cover_art_url,
        uploaded_date: null,
        created_at: null,
        // A log entry is a moment in time, not a position in a list.
        position: null,
    };
}

/**
 * Messages can be huge (trackinfo command output is the entire file in one
 * entry, bounded only by DynamoDB's 400KB item limit) — cap what reaches the
 * DOM and the native tooltip. The message is shown clamped to 3 lines with a
 * Show more toggle (see LzpLogMessage), so it gets a generous cap: enough for
 * "many lines" when expanded without dumping a 400KB entry into the DOM.
 */
const MESSAGE_EXPANDED_CHARS = 5000;
const MESSAGE_PREVIEW_CHARS = 300;
const TOOLTIP_CHARS = 1000;

function clamp(text: string, max: number): string {
    return text.length > max ? `${text.slice(0, max)}…` : text;
}

/**
 * Primary line: the raw message when present. Entries whose track resolved
 * render the track row beneath instead, so a play-only entry needs no text
 * line; codes that resolve to nothing still fall back to plain text.
 */
function logText(log: PlayerLogEntry): string | null {
    if (log.message !== null) {
        return clamp(log.message, MESSAGE_EXPANDED_CHARS);
    }
    if (log.track !== null) {
        return null;
    }
    return clamp(log.track_name ?? log.track_code ?? 'Log entry', MESSAGE_PREVIEW_CHARS);
}

/** Tooltip carries (a bounded slice of) everything the row truncated away. */
function logTitle(log: PlayerLogEntry): string | undefined {
    const parts = [log.message, log.track_name ?? log.track_code].filter(
        (part, index, all) => part && all.indexOf(part) === index
    );
    return parts.length > 0 ? clamp((parts as string[]).join(' · '), TOOLTIP_CHARS) : undefined;
}

const sections = useInspectorSections('LocationZonePlayer');

// Open → the card flex-fills the inspector's leftover vertical space (the
// entries list absorbs it, see the <ul>); collapsed → shrink back to the
// trigger row instead of holding an empty stretched box.
const logsSectionOpen = computed(() => sections.isOpen('Logs'));
</script>

<template>
    <Collapsible
        :open="logsSectionOpen"
        class="border-border/80 bg-muted/20 rounded-lg border px-3 py-2"
        :class="logsSectionOpen ? 'flex min-h-0 flex-1 flex-col' : 'flex-none'"
        data-testid="lzp-inspector-logs"
        @update:open="sections.setOpen('Logs', $event)"
    >
        <CollapsibleTrigger
            class="group focus-visible:ring-ring flex w-full items-center justify-between gap-2 text-left outline-none focus-visible:ring-1"
            data-testid="lzp-logs-trigger"
        >
            <h4 class="text-sm font-semibold">Logs</h4>
            <ChevronDownIcon
                class="text-muted-foreground size-3.5 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180"
                aria-hidden="true"
            />
        </CollapsibleTrigger>
        <!-- Flex column so the entries list can absorb the card's leftover height
             (hidden entirely while collapsed, so the classes only act when open). -->
        <CollapsibleContent class="flex min-h-0 flex-1 flex-col">
            <div class="mt-2" data-testid="lzp-logs-filters">
                <LzpLogFilterRules
                    v-model:range="range"
                    v-model:message-filter="messageFilter"
                    v-model:message-mode="messageMode"
                    v-model:track-filter="trackFilter"
                    v-model:types="types"
                    v-model:types-mode="typesMode"
                    :available-types="props.meta?.types ?? []"
                />
            </div>
            <p v-if="isLoading" class="text-muted-foreground mt-1 text-xs">Loading…</p>
            <p v-else-if="isError" class="text-destructive mt-1 text-xs">
                Couldn't load player logs.
            </p>
            <p v-else-if="logs.length === 0" class="text-muted-foreground mt-1 text-xs">
                {{ filtersActive ? 'No logs match the current filters.' : 'No logs.' }}
            </p>
            <!-- flex-1 replaces the old max-h-96 cap: the list grows to fill the card's
                 leftover height; min-h-40 keeps a usable floor when space is tight. In hosts
                 whose height chain is bounded (split-view pane, wide rail) flex-1 caps the
                 list and it scrolls internally; in auto-height hosts (side-peek / overlay
                 scrollports) flex-1 alone would grow to full content height, so max-h-[70vh]
                 keeps the list a windowed, internally-scrolling region there too. -->
            <ul v-else class="mt-2 flex max-h-[70vh] min-h-40 flex-1 flex-col gap-1.5 overflow-auto pb-1 pr-1">
                <!-- One bordered card per entry, holding the message AND its track.
                     The card used to be a bare zebra stripe with the track card
                     drawn on top of it, so an opaque white card sat in the middle
                     of a tinted row and read as a separate thing. The tint is on
                     the entry itself now, the track sits on that same surface, and
                     there is no rule between them — the border does the grouping. -->
                <li
                    v-for="(log, index) in logs"
                    :key="`${log.time}-${index}`"
                    class="border-border/60 odd:bg-foreground/[0.04] flex flex-col gap-1.5 rounded-lg border px-2.5 py-2"
                >
                    <!-- Every entry has a message; a track only ever accompanies
                         one. The timestamp floats top-right and the message wraps
                         around it, clamped to 3 lines then expandable. -->
                    <LzpLogMessage
                        v-if="logText(log) !== null"
                        :text="logText(log) ?? ''"
                        :date="formatUsMmDdYyTime12h(log.logged_at)"
                        :title="logTitle(log)"
                        :playlist-name="playlistFileNameInLogMessage(log.message)"
                        @open-playlist="openPlaylist"
                    />
                    <!-- An entry with no message still needs its time shown. -->
                    <span
                        v-else
                        class="text-muted-foreground self-end text-[11px] leading-4 tabular-nums"
                    >
                        {{ formatUsMmDdYyTime12h(log.logged_at) }}
                    </span>
                    <TrackCard
                        v-if="log.track !== null"
                        :track="logTrackRow(log.track)"
                        density="compact"
                        flush
                        data-testid="lzp-log-track-row"
                    />
                </li>
            </ul>
            <p
                v-if="!isError && props.meta?.scan_limited"
                class="text-muted-foreground mt-1 text-[11px]"
                data-testid="lzp-logs-scan-limited"
            >
                Searched the most recent {{ props.meta.scanned_count.toLocaleString() }} entries —
                older entries weren't searched.
            </p>
            <p
                v-if="!isError && props.meta?.track_codes_limited"
                class="text-muted-foreground mt-1 text-[11px]"
                data-testid="lzp-logs-track-codes-limited"
            >
                More than 50 tracks matched — narrow the track filter to see all results.
            </p>
        </CollapsibleContent>
    </Collapsible>
</template>
